import { useZinsserStore } from '@/stores/useZinsserStore'
import { FileText } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Markdown } from 'tiptap-markdown'
import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { Addition, Deletion, Note, parseAnnotations, serializeAnnotations } from '@/components/editor/AnnotationExtension'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { WritingStats } from '@/components/editor/WritingStats'
import { updateZinsserSession } from '@/lib/api'
// stripAnnotations no longer needed - consolidated to single new_doc field

// Auto-save configuration
const AUTO_SAVE_DELAY = 1500 // 1.5 seconds debounce
let foolscapAutoSaveTimer: ReturnType<typeof setTimeout> | null = null

interface ZinsserFoolscapDocumentContentProps {
  editable?: boolean
}

export function ZinsserFoolscapDocumentContent({ editable: editableProp = true }: ZinsserFoolscapDocumentContentProps) {
  const {
    sessionId,
    foolscapContent,
    foolscapDisplayContent,
    foolscapDocumentStreaming,
    foolscapDocumentPending,
    foolscapStreamingLineCount,
    workflowStatus,
    updateFoolscapContent,
  } = useZinsserStore()
  const isMobile = useIsMobile()
  const [copied, setCopied] = useState(false)

  // Lock editing during document streaming (per design decision)
  const editable = editableProp && !foolscapDocumentStreaming && !foolscapDocumentPending

  // Document display: Use transient displayContent if available, fall back to persisted content
  // - foolscapDisplayContent: Set by streaming actions (not persisted, avoids race conditions)
  // - foolscapContent: Persisted to localStorage, set after streaming is fully complete
  // - Annotations (==additions==, ~~deletions~~) are rendered as highlights/strikethroughs
  const displayContent = foolscapDisplayContent ?? foolscapContent

  const editorContainerRef = useRef<HTMLDivElement>(null)
  const prevContentRef = useRef<string>('')
  const isExternalUpdateRef = useRef<boolean>(false)
  const isUserEditingRef = useRef<boolean>(false)
  // Track last saved content to prevent redundant saves and empty content wipes
  const lastSavedContentRef = useRef<string | null>(null)

  // Strip card UUIDs from display (keep in raw content for tracking)
  const stripCardIds = (content: string): string => {
    return content.replace(/\s*\[[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\]/gi, '')
  }

  // Parse annotation markers to HTML before passing to TipTap
  const parsedContent = useMemo(() => {
    if (!displayContent) return ''
    const strippedContent = stripCardIds(displayContent)
    return parseAnnotations(strippedContent)
  }, [displayContent])

  // Auto-save to database with debounce
  // CRITICAL: Only save if content is non-empty and different from last saved
  const autoSave = useCallback(async (content: string) => {
    if (!sessionId) return

    // Don't save empty content - this prevents wiping DB on hydration race
    if (!content || content.trim() === '') return

    // Don't save if content hasn't changed from last save
    if (lastSavedContentRef.current === content) return

    try {
      // Note: In consolidated schema, we only have new_doc for canvas content
      // Foolscap shares the same field as brainstorm for now
      await updateZinsserSession(sessionId, {
        new_doc: content,
      })
      lastSavedContentRef.current = content
    } catch (err) {
      console.error('[ZinsserFoolscapDoc] Auto-save failed:', err)
    }
  }, [sessionId])

  // Handle user edits - sync back to store and trigger auto-save
  const handleEditorUpdate = useCallback(({ editor }: { editor: ReturnType<typeof useEditor> }) => {
    // Don't process edits during external updates or when not editable
    if (isExternalUpdateRef.current || !editable) return

    // Mark that user is actively editing - prevents setContent from firing
    isUserEditingRef.current = true

    // Use tiptap-markdown to get proper markdown output (not HTML)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    if (storage?.markdown?.getMarkdown) {
      const markdown = storage.markdown.getMarkdown()
      // Convert HTML annotation marks back to markdown format
      const serialized = serializeAnnotations(markdown)

      if (serialized !== foolscapContent) {
        updateFoolscapContent(serialized)

        // Debounced auto-save to database
        if (foolscapAutoSaveTimer) {
          clearTimeout(foolscapAutoSaveTimer)
        }
        foolscapAutoSaveTimer = setTimeout(() => {
          autoSave(serialized)
          // Reset user editing flag after auto-save completes
          isUserEditingRef.current = false
        }, AUTO_SAVE_DELAY)
      }
    }
  }, [foolscapContent, updateFoolscapContent, autoSave, editable])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Markdown.configure({
        html: true,  // Allow HTML tags within markdown (for annotation marks)
        transformPastedText: true,
        transformCopiedText: true,
        linkify: false,  // Disable link handling to avoid duplicate with @tiptap/extension-link
      }),
      Placeholder.configure({
        placeholder: 'Your Foolscap outline...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Addition,
      Deletion,
      Note,
    ],
    content: parsedContent || '',  // Initialize with content (matches develop pattern)
    editable,
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none min-h-full',
      },
    },
    onUpdate: handleEditorUpdate,
    immediatelyRender: false,
  }, [])  // Empty deps - only create editor once

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  // Update editor content and trigger animations when content changes (matches develop pattern)
  useEffect(() => {
    if (!editor) return

    const contentChanged = prevContentRef.current !== parsedContent
    prevContentRef.current = parsedContent

    // CRITICAL: Skip setContent entirely when user is actively editing
    // This prevents scroll jumping on mobile when auto-save triggers store updates
    if (isUserEditingRef.current && !foolscapDocumentStreaming) {
      return
    }

    // No content change and not streaming - nothing to update
    if (!contentChanged && !foolscapDocumentStreaming) {
      return
    }

    isExternalUpdateRef.current = true
    // Use emitUpdate: false to prevent triggering onUpdate during external content set
    // The content contains markdown with HTML marks for annotations - tiptap-markdown handles both
    editor.commands.setContent(parsedContent || '', { emitUpdate: false })
    queueMicrotask(() => {
      isExternalUpdateRef.current = false
    })

    // Trigger animations on new annotations after content update
    if (contentChanged && parsedContent && editorContainerRef.current) {
      requestAnimationFrame(() => {
        const container = editorContainerRef.current
        if (!container) return

        const additions = container.querySelectorAll('.annotation-addition')
        const deletions = container.querySelectorAll('.annotation-deletion')

        additions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
        deletions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
      })
    }
  }, [editor, parsedContent])

  // Highlight last block during streaming for progress indication
  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    // Get all block-level elements (paragraphs, headings, list items)
    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    // If not streaming, remove all highlights
    if (!foolscapDocumentStreaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    // Remove highlight from all blocks first
    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    // Highlight the LAST block element (where new content is being added)
    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
      // Auto-scroll to keep the last block visible (desktop only)
      // On mobile, this causes the text to jump when keyboard is open
      if (!isMobile) {
        lastBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    // Cleanup: remove highlights when effect re-runs or unmounts
    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [foolscapDocumentStreaming, foolscapStreamingLineCount])

  return (
    <div className="h-full flex flex-col">
      {/* Editing indicator - shows at top when streaming */}
      {foolscapDocumentStreaming && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-red-500/30 bg-red-500/10">
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-medium">Editing document...</span>
          </div>
        </div>
      )}

      {/* Toolbar - only show when editable and not streaming */}
      {editable && !foolscapDocumentStreaming && (
        <EditorToolbar editor={editor} disabled={!editable} />
      )}

      {/* Document content */}
      <div
        className="flex-1 overflow-y-auto px-4 pt-3 pb-32 md:pb-[30vh] relative cursor-text"
        onClick={() => editor?.commands.focus()}
      >
        {displayContent ? (
          <div className="min-h-full" ref={editorContainerRef}>
            <EditorContent editor={editor} className="min-h-full" />
          </div>
        ) : (foolscapDocumentPending || foolscapDocumentStreaming) ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <div className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Building document...
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <FileText className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Your Foolscap outline will appear here
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Type /foolscap to transform your brainstorm
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Writing stats - show when not streaming */}
      {!foolscapDocumentStreaming && workflowStatus !== 'streaming' && (
        <WritingStats content={foolscapContent || ''} />
      )}

      {/* Copy button - show when not actively streaming */}
      {foolscapContent && workflowStatus !== 'streaming' && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border">
          <button
            onClick={() => {
              navigator.clipboard.writeText(foolscapContent)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
            className={`w-full text-xs text-center py-1 transition-colors ${
              copied
                ? 'text-green-600 dark:text-green-400 bg-green-500/10'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {copied ? 'Copied!' : 'Click to copy Foolscap'}
          </button>
        </div>
      )}
    </div>
  )
}
